import { Controller } from 'stimulus';

// Wraps a remote-mode `tabulator-table` together with filter controls
// (date-range shortcuts, custom date-range form, floor filter, …) that
// would otherwise full-page-reload to apply. Intercepts those
// interactions, updates the URL via `history.pushState`, and dispatches
// `tabulator-table:refresh` on the table element.
//
// `tabulator_table_controller.js#buildAjaxUrl` already pulls query
// params from `window.location.href` on every request, so the table's
// AJAX picks up the new constraints automatically. Sort / filter /
// search / page state on the JS side is untouched — that's the win.
//
// When an interaction doesn't match the JS-happy path (e.g. a date form
// missing its type field, a navigate without an href), the handler
// returns early *before* `preventDefault`, so the browser's native
// submit / navigation runs and the server-side error path still works.
//
// Date-range CLEARED_PARAMS keys mirror `WithDateRange::CLEARED_PARAMS`
// so the JS path produces the same URL the server's
// `redirect_url` would.
const DATE_RANGE_CLEARED_PARAMS = [
  'single_date',
  'start_date',
  'end_date',
  'start_time',
  'end_time',
  'duration_amount',
  'duration_unit',
  'duration_type',
];

// Path-style date suffixes (matching the routes in `config/routes.rb`:
// `last-:duration_amount-:duration_unit`, `:start_date/:end_date`,
// `:single_date`). Stripped before pushing a new custom date range so
// that `set_range_from_duration_params` on the server doesn't see the
// old duration and overwrite the new explicit start/end dates.
// Equivalent to what `url_for(**CLEARED_PARAMS, …)` does on the Ruby
// side after a POST → redirect.
const DATE_PATH_SUFFIX_RES = [
  /\/last-\d+-\w+$/,
  /\/\d{4}-\d{2}-\d{2}\/\d{4}-\d{2}-\d{2}$/,
  /\/\d{4}-\d{2}-\d{2}$/,
];

export default class extends Controller {
  static targets = ['filters'];

  navigate(event) {
    // Preserve modifier-clicks (cmd/ctrl/shift/alt) and middle/right
    // clicks — those should still open in a new tab / context-menu.
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    const href = event.currentTarget?.href;
    if (!href) return;
    event.preventDefault();
    this.refreshTo(href);
  }

  // Bound to `change` on an input inside a GET form that should auto-
  // submit (e.g. the floor `<select>`). Programmatically triggers form
  // submit so the form's own `submit->submitGetForm` action handles
  // the rest — same pattern as the `submit-on-action` controller it
  // replaces in this mode.
  submitOnChange(event) {
    const form = event.currentTarget.closest('form');
    if (!form) return;
    form.requestSubmit();
  }

  // Floor filter (and any other GET form whose visible inputs override
  // params on the current URL): overlay each form value on top of
  // `window.location` and refresh. Reading the base from the current URL
  // (rather than the form's hidden inputs) is what prevents stale
  // date-range hiddens from clobbering an in-flight pushState — the
  // address bar is the source of truth.
  submitGetForm(event) {
    const form = event.target.closest('form');
    if (!form) return;
    event.preventDefault();

    const url = new URL(window.location.href);
    new FormData(form).forEach((value, key) => {
      if (typeof value !== 'string') return;
      url.searchParams.set(key, value);
    });
    this.refreshTo(url.toString());
  }

  // Custom date-range POST form: rebuild the GET URL the server would
  // have redirected to and refresh in place. The `data-action` is on
  // the FormComponent's wrapper `<div>` (its template renders
  // `= root(...) do = simple_form_for`), so we resolve the actual
  // `<form>` from the bubbled event target. Returns early — letting the
  // native POST proceed — when the form is missing the type/date
  // fields we expect; the server-side error path then owns the
  // malformed submission.
  submitDateRangeForm(event) {
    const form = event.target.closest('form');
    if (!form) return;

    const data = new FormData(form);
    const type = data.get('date_range[type]');
    const startDate = data.get('date_range[start_date]');
    const endDate = data.get('date_range[end_date]');
    const singleDate = data.get('date_range[single_date]');

    const url = new URL(window.location.href);
    DATE_PATH_SUFFIX_RES.forEach((re) => {
      url.pathname = url.pathname.replace(re, '');
    });
    DATE_RANGE_CLEARED_PARAMS.forEach((k) => url.searchParams.delete(k));

    if (type === 'date_range' && startDate && endDate) {
      url.searchParams.set('start_date', startDate);
      url.searchParams.set('end_date', endDate);
    } else if (type === 'single_date' && singleDate) {
      url.searchParams.set('single_date', singleDate);
    } else {
      return; // fall through to native POST
    }

    event.preventDefault();
    this.refreshTo(url.toString());
  }

  refreshTo(url) {
    window.history.pushState(null, '', url);
    const table = this.element.querySelector(
      '[data-controller~="tabulator-table"]'
    );
    if (!table) {
      // No table inside our wrapper — fall back to a real navigation so
      // the user's action still does something visible.
      window.location.href = url;
      return;
    }
    table.dispatchEvent(
      new CustomEvent('tabulator-table:refresh', { bubbles: true })
    );
    // Reconcile filter UI with the new URL by re-fetching the page's
    // HTML and swapping the filter row in. Server-rendered state (active
    // shortcut, visible date range text, custom-range form initial
    // values, floor filter's hidden start/end_date inputs) all stays in
    // sync without duplicating server logic on the JS side. The table
    // element is a sibling of the filters target, so swapping doesn't
    // touch Tabulator.
    if (this.hasFiltersTarget) this.refreshFiltersFromServer(url);
  }

  async refreshFiltersFromServer(url) {
    let response;
    try {
      response = await fetch(url, {
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
          // Contract with the server: render *only* the filter row
          // partial, no layout, no expensive sibling queries. Header
          // (rather than a query param) so it doesn't leak into the
          // address bar or get echoed into rendered links.
          'X-Tabulator-Filters-Only': '1',
        },
        credentials: 'same-origin',
      });
    } catch (error) {
      console.warn(
        '[tabulator-table-refresher] failed to refetch filter HTML:',
        error
      );
      return;
    }
    // Accept 422 too — `WithDateRange#invalid_submitted_range` re-renders
    // the index page with error markers using that status, and we still
    // want those markers swapped in.
    if (!response.ok && response.status !== 422) return;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incoming = doc.querySelector(
      '[data-tabulator-table-refresher-target="filters"]'
    );
    if (!incoming) return;
    this.filtersTarget.replaceWith(incoming);
  }
}
